import { useRef, useEffect, useState, useCallback, useMemo } from 'react';

export interface AnimationConfig {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
  delay?: number;
  duration?: number;
  easing?: string;
  direction?: 'up' | 'down' | 'left' | 'right' | 'scale' | 'fade' | 'rotate';
  distance?: number;
  stagger?: number;
  reverseOnExit?: boolean;
}

export interface AnimationState {
  isVisible: boolean;
  isAnimating: boolean;
  hasAnimated: boolean;
  direction: 'enter' | 'exit';
}

const defaultConfig: Required<AnimationConfig> = {
  threshold: 0.1,
  rootMargin: '0px 0px -10% 0px',
  triggerOnce: false,
  delay: 0,
  duration: 800,
  easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
  direction: 'up',
  distance: 60,
  stagger: 0,
  reverseOnExit: true,
};

export function useAdvancedAnimations<T extends HTMLElement>(
  config: AnimationConfig = {}
) {
  const ref = useRef<T | null>(null);
  const [state, setState] = useState<AnimationState>({
    isVisible: false,
    isAnimating: false,
    hasAnimated: false,
    direction: 'enter',
  });

  const mergedConfig = useMemo(() => ({ ...defaultConfig, ...config }), [config]);

  const updateState = useCallback((newState: Partial<AnimationState>) => {
    setState(prev => ({ ...prev, ...newState }));
  }, []);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const isIntersecting = entry.isIntersecting;
        const wasVisible = state.isVisible;
        
        if (isIntersecting && !wasVisible) {
          // Element entering viewport
          updateState({
            isVisible: true,
            isAnimating: true,
            direction: 'enter',
          });

          // Add stagger delay if configured
          setTimeout(() => {
            updateState({ isAnimating: false });
            if (mergedConfig.triggerOnce) {
              updateState({ hasAnimated: true });
            }
          }, mergedConfig.delay + mergedConfig.duration);
        } else if (!isIntersecting && wasVisible && mergedConfig.reverseOnExit && !mergedConfig.triggerOnce) {
          // Element exiting viewport (reverse animation)
          updateState({
            isVisible: false,
            isAnimating: true,
            direction: 'exit',
          });

          setTimeout(() => {
            updateState({ isAnimating: false });
          }, mergedConfig.duration);
        }
      });
    },
    [state.isVisible, mergedConfig, updateState]
  );

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(handleIntersection, {
      threshold: mergedConfig.threshold,
      rootMargin: mergedConfig.rootMargin,
    });

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [handleIntersection, mergedConfig.threshold, mergedConfig.rootMargin]);

  // Generate CSS animation properties
  const getAnimationStyles = useCallback(() => {
    const { direction, distance, duration, easing, delay } = mergedConfig;
    const { isVisible, isAnimating, direction: animDirection } = state;

    if (!isAnimating && !isVisible && mergedConfig.triggerOnce && state.hasAnimated) {
      return {};
    }

    const isEntering = animDirection === 'enter';

    const transforms = {
      up: `translateY(${isEntering ? 0 : distance}px)`,
      down: `translateY(${isEntering ? 0 : -distance}px)`,
      left: `translateX(${isEntering ? 0 : distance}px)`,
      right: `translateX(${isEntering ? 0 : -distance}px)`,
      scale: `scale(${isEntering ? 1 : 0.8})`,
      fade: 'translateY(0)',
      rotate: `rotate(${isEntering ? 0 : 5}deg)`,
    };

    return {
      opacity: isEntering ? (isVisible ? 1 : 0) : (isVisible ? 0 : 1),
      transform: transforms[direction],
      transition: `all ${duration}ms ${easing} ${delay}ms`,
      willChange: isAnimating ? 'transform, opacity' : 'auto',
    };
  }, [state, mergedConfig]);

  return {
    ref,
    ...state,
    animationStyles: getAnimationStyles(),
    config: mergedConfig,
  };
}

// Hook para animações em sequência (stagger)
export function useStaggeredAnimations<T extends HTMLElement>(
  items: unknown[],
  config: AnimationConfig = {}
) {
  const refs = useRef<(T | null)[]>([]);
  const [visibleItems, setVisibleItems] = useState<boolean[]>([]);

  const mergedConfig = useMemo(() => ({ ...defaultConfig, ...config }), [config]);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    refs.current.forEach((element, index) => {
      if (!element) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleItems(prev => {
                const newItems = [...prev];
                newItems[index] = true;
                return newItems;
              });
            }
          });
        },
        {
          threshold: mergedConfig.threshold,
          rootMargin: mergedConfig.rootMargin,
        }
      );

      observer.observe(element);
      observers.push(observer);
    });

    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, [items.length, mergedConfig.threshold, mergedConfig.rootMargin]);

  const getItemAnimationStyles = useCallback((index: number) => {
    const isVisible = visibleItems[index] || false;
    const { direction, distance, duration, easing, stagger } = mergedConfig;
    const delay = index * (stagger || 100);

    const transforms = {
      up: `translateY(${isVisible ? 0 : distance}px)`,
      down: `translateY(${isVisible ? 0 : -distance}px)`,
      left: `translateX(${isVisible ? 0 : distance}px)`,
      right: `translateX(${isVisible ? 0 : -distance}px)`,
      scale: `scale(${isVisible ? 1 : 0.8})`,
      fade: 'translateY(0)',
      rotate: `rotate(${isVisible ? 0 : 5}deg)`,
    };

    return {
      opacity: isVisible ? 1 : 0,
      transform: transforms[direction],
      transition: `all ${duration}ms ${easing} ${delay}ms`,
      willChange: 'transform, opacity',
    };
  }, [visibleItems, mergedConfig]);

  return {
    refs,
    getItemAnimationStyles,
    visibleItems,
  };
}

// Hook para animações de scroll suave com parallax
export function useParallaxScroll<T extends HTMLElement>(
  speed: number = 0.5,
  direction: 'vertical' | 'horizontal' = 'vertical'
) {
  const ref = useRef<T | null>(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!ref.current) return;

      const scrolled = window.pageYOffset;
      const rate = scrolled * -speed;

      if (direction === 'vertical') {
        setOffset(rate);
      } else {
        setOffset(rate);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial call

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [speed, direction]);

  const getParallaxStyles = useCallback(() => {
    return {
      transform: direction === 'vertical' 
        ? `translateY(${offset}px)` 
        : `translateX(${offset}px)`,
      willChange: 'transform',
    };
  }, [offset, direction]);

  return {
    ref,
    parallaxStyles: getParallaxStyles(),
  };
}

// Hook para animações de hover avançadas
export function useHoverAnimations<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!ref.current) return;

    const x = e.clientX - ref.current.getBoundingClientRect().left;
    const y = e.clientY - ref.current.getBoundingClientRect().top;
    
    setMousePosition({ x, y });
  }, []);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('mouseleave', handleMouseLeave);
    element.addEventListener('mousemove', handleMouseMove);

    return () => {
      element.removeEventListener('mouseenter', handleMouseEnter);
      element.removeEventListener('mouseleave', handleMouseLeave);
      element.removeEventListener('mousemove', handleMouseMove);
    };
  }, [handleMouseEnter, handleMouseLeave, handleMouseMove]);

  const getHoverStyles = useCallback(() => {
    const intensity = isHovered ? 1 : 0;
    const tiltX = (mousePosition.y - 50) / 50 * 10 * intensity;
    const tiltY = (mousePosition.x - 50) / 50 * -10 * intensity;

    return {
      transform: `perspective(1000px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) scale(${1 + intensity * 0.05})`,
      transition: 'transform 0.3s cubic-bezier(0.25, 0.1, 0.25, 1)',
      willChange: 'transform',
    };
  }, [isHovered, mousePosition]);

  return {
    ref,
    isHovered,
    hoverStyles: getHoverStyles(),
  };
}
